/**
 * Copyright (c) HashiCorp, Inc.
 */

import Component from '@glimmer/component';
import { and, eq, not, notEq } from 'ember-truth-helpers';
import {
  NORMALIZED_GATEWAY_LABELS,
  SERVICE_GATEWAY_TYPE,
} from '../../../utils/service-list-item.ts';
import CutListItem from '../list-item.gts';
import ListItemTemplate from './template.gts';
import MetadataClusterPath from '../metadata/cluster-path.gts';
import MetadataServiceHealthBadge from '../metadata/service-health-badge.gts';
import CutTextWithIcon from '../text-with-icon.gts';
import MetadataInServiceMesh from '../metadata/in-service-mesh.gts';
import MetadataExternalSource from '../metadata/external-source.gts';
import MetadataTags from '../metadata/tags.gts';

import type { CutService } from '../list-item.gts';

export interface ListItemServiceSignature {
  Args: {
    // List item args;
    href?: string;
    isHrefExternal?: boolean;
    route?: string;
    isRouteExternal?: boolean;
    query?: Record<string, unknown> | undefined;
    replace?: boolean;
    model?: string;
    models?: string[] | unknown[];
    onClick?: (...args: unknown[]) => void;

    // Service args
    service: CutService;
    hideClusterPath?: boolean;
  };
}

export default class ListItemService extends Component<ListItemServiceSignature> {
  ServiceGatewayType = SERVICE_GATEWAY_TYPE;
  NormalizedGatewayLabels = NORMALIZED_GATEWAY_LABELS;

  get isIngressGateway() {
    return (
      this.args.service.metadata?.kind ===
      this.ServiceGatewayType.IngressGateway
    );
  }

  get isTerminatingGateway() {
    return (
      this.args.service.metadata?.kind ===
      this.ServiceGatewayType.TerminatingGateway
    );
  }

  get kindName() {
    const kind = this.args.service.metadata?.kind;
    return kind &&
      Object.prototype.hasOwnProperty.call(this.NormalizedGatewayLabels, kind)
      ? this.NormalizedGatewayLabels[
          kind as
            | 'api-gateway'
            | 'mesh-gateway'
            | 'ingress-gateway'
            | 'terminating-gateway'
        ]
      : undefined;
  }

  <template>
    <CutListItem
      @href={{@href}}
      @isRouteExternal={{@isRouteExternal}}
      @route={{@route}}
      @model={{@model}}
      @models={{@models}}
      @isHrefExternal={{@isHrefExternal}}
      @query={{@query}}
      @replace={{@replace}}
      @onClick={{@onClick}}
      data-test-service-list-item
      as |L|
    >
      <L.Content>
        <ListItemTemplate as |T|>
          <T.Section as |SC|>
            <SC.Title data-test-service-name>
              {{@service.name}}
              {{#unless @hideClusterPath}}
                <MetadataClusterPath
                  @clusterId={{@service.metadata.clusterId}}
                  @partition={{@service.metadata.partition}}
                  @namespace={{@service.metadata.namespace}}
                />
              {{/unless}}
            </SC.Title>
            <SC.Metadata>
              <MetadataServiceHealthBadge
                @successCount={{@service.metadata.healthCheck.instance.success}}
                @warningCount={{@service.metadata.healthCheck.instance.warning}}
                @criticalCount={{@service.metadata.healthCheck.instance.critical}}
              />
              {{#if this.kindName}}
                <CutTextWithIcon
                  @icon="gateway"
                  @text={{this.kindName}}
                  data-test-service-kind
                />
              {{/if}}
              {{#if this.isIngressGateway}}
                {{#if (notEq @service.metadata.upstreamCount undefined)}}
                  {{! TODO: add translate with pluralize(CC-5314)}}
                  <span
                    data-test-associated-upstream-count
                  >{{@service.metadata.upstreamCount}} upstreams</span>
                {{/if}}
              {{else if this.isTerminatingGateway}}
                {{#if (notEq @service.metadata.linkedServiceCount undefined)}}
                  {{! TODO: add translate with pluralize (CC-5314) }}
                  <span
                    data-test-associated-service-count
                  >{{@service.metadata.linkedServiceCount}}
                    linked services</span>
                {{/if}}
              {{else}}
                {{#if (notEq @service.metadata.instanceCount undefined)}}
                  {{! TODO: add translate with pluralize(CC-5314) }}
                  <span
                    data-test-associated-instance-count
                  >{{@service.metadata.instanceCount}} instances</span>
                {{/if}}
              {{/if}}
              {{#if @service.metadata.isPermissiveMTls}}
                {{! TODO: add translate(CC-5314) }}
                <CutTextWithIcon
                  @icon="unlock"
                  @text="permissive mTLS"
                  data-test-permissive-mtls
                />
              {{else if
                (and
                  (eq @service.metadata.isPermissiveMTls false)
                  (not (eq @service.metadata.isPermissiveMTls undefined))
                )
              }}
                <CutTextWithIcon
                  @icon="lock"
                  @text="strict mTLS"
                  data-test-strict-mtls
                />
              {{/if}}
              <MetadataInServiceMesh
                @connectedWithGateway={{@service.metadata.connectedWithGateway}}
                @connectedWithProxy={{@service.metadata.connectedWithProxy}}
              />
              {{#if @service.metadata.samenessGroup}}
                <CutTextWithIcon
                  @icon="collections"
                  @text={{@service.metadata.samenessGroup}}
                  data-test-sameness-group
                />
              {{/if}}
              {{#if @service.metadata.isImported}}
                {{! TODO: add translate(CC-5314) }}
                <CutTextWithIcon
                  @icon="upload"
                  @text="Imported"
                  data-test-imported
                />
              {{/if}}
              {{#if @service.metadata.externalSource}}
                <MetadataExternalSource
                  @externalSource={{@service.metadata.externalSource}}
                />
              {{/if}}
              {{#if @service.metadata.tags.length}}
                <MetadataTags @tags={{@service.metadata.tags}} />
              {{/if}}
            </SC.Metadata>
          </T.Section>
        </ListItemTemplate>
      </L.Content>
    </CutListItem>
  </template>
}
